import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "../Select";

export type RecurrenceFreq = "DAILY" | "WEEKLY" | "MONTHLY";
const WEEKDAYS: { code: string; key: string; label: string }[] = [
  { code: "MO", key: "tm.recurrence.weekday.mon", label: "Mon" },
  { code: "TU", key: "tm.recurrence.weekday.tue", label: "Tue" },
  { code: "WE", key: "tm.recurrence.weekday.wed", label: "Wed" },
  { code: "TH", key: "tm.recurrence.weekday.thu", label: "Thu" },
  { code: "FR", key: "tm.recurrence.weekday.fri", label: "Fri" },
  { code: "SA", key: "tm.recurrence.weekday.sat", label: "Sat" },
  { code: "SU", key: "tm.recurrence.weekday.sun", label: "Sun" },
];

/**
 * Form fragment that builds an RRULE-subset string. The parent treats the
 * output as opaque — if {@code enabled} is false it passes null to the BE
 * and the meeting saves as a one-off.
 *
 * Output formats:
 * FREQ=DAILY;INTERVAL=1
 * FREQ=WEEKLY;INTERVAL=2;BYDAY=MO,WE,FR
 * FREQ=MONTHLY;INTERVAL=1
 * …optionally appended with ;COUNT=12 or ;UNTIL=20260605T120000Z
 */
export function TmRecurrenceForm({
  value, onChange,
}: {
  value: string | null;
  onChange: (rule: string | null) => void;
}) {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(value != null);
  const [freq, setFreq] = useState<RecurrenceFreq>("WEEKLY");
  const [interval, setInterval] = useState(1);
  const [byDay, setByDay] = useState<Set<string>>(new Set(["MO"]));
  const [endMode, setEndMode] = useState<"never" | "count" | "until">("never");
  const [count, setCount] = useState(10);
  const [until, setUntil] = useState("");

  useEffect(() => {
    if (!enabled) { onChange(null); return; }
    const parts: string[] = [`FREQ=${freq}`, `INTERVAL=${Math.max(1, interval)}`];
    if (freq === "WEEKLY" && byDay.size > 0) {
      parts.push(`BYDAY=${WEEKDAYS.filter((d) => byDay.has(d.code)).map((d) => d.code).join(",")}`);
    }
    if (endMode === "count")      parts.push(`COUNT=${Math.max(1, count)}`);
    else if (endMode === "until" && until) {
      // RFC form requires a UTC datetime stamp; treat the picked date as end-of-day local.
      const d = new Date(`${until}T23:59:59`);
      const stamp = d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
      parts.push(`UNTIL=${stamp}`);
    }
    onChange(parts.join(";"));
  }, [enabled, freq, interval, byDay, endMode, count, until, onChange]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--ink-2)" }}>
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span>{t("tm.recurrence.repeat", "Repeat this meeting")}</span>
      </label>

      {enabled && (
        <div style={panel}>
          <div style={row}>
            <label style={field}>
              <span style={fieldLabel}>{t("tm.recurrence.frequency", "Frequency")}</span>
              <Select<RecurrenceFreq>
                value={freq}
                onChange={setFreq}
                ariaLabel={t("tm.recurrence.frequency", "Frequency")}
                options={[
                  { value: "DAILY", label: t("tm.recurrence.daily", "Daily") },
                  { value: "WEEKLY", label: t("tm.recurrence.weekly", "Weekly") },
                  { value: "MONTHLY", label: t("tm.recurrence.monthly", "Monthly") },
                ]}
              />
            </label>
            <label style={field}>
              <span style={fieldLabel}>{t("tm.recurrence.every", "Every")}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="number"
                  min="1"
                  max="60"
                  className="input"
                  value={interval}
                  onChange={(e) => setInterval(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  style={{ width: 70 }}
                />
                <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
                  {freq === "DAILY"
                    ? t("tm.recurrence.unit.days", "day(s)")
                    : freq === "WEEKLY"
                      ? t("tm.recurrence.unit.weeks", "week(s)")
                      : t("tm.recurrence.unit.months", "month(s)")}
                </span>
              </div>
            </label>
          </div>

          {freq === "WEEKLY" && (
            <div>
              <div style={fieldLabel}>{t("tm.recurrence.on", "On")}</div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {WEEKDAYS.map((d) => {
                  const active = byDay.has(d.code);
                  return (
                    <button
                      key={d.code}
                      type="button"
                      onClick={() => setByDay((prev) => {
                        const next = new Set(prev);
                        if (next.has(d.code)) next.delete(d.code); else next.add(d.code);
                        return next;
                      })}
                      style={{
                        padding: "5px 10px",
                        borderRadius: 6,
                        border: `1px solid ${active ? "var(--brand-primary)" : "var(--border)"}`,
                        background: active ? "var(--brand-primary)" : "var(--surface)",
                        color: active ? "var(--on-primary)" : "var(--ink-2)",
                        fontSize: 12, fontWeight: 600, cursor: "pointer",
                      }}
                    >
                      {t(d.key, d.label)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <div style={fieldLabel}>{t("tm.recurrence.ends", "Ends")}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={radioRow}>
                <input type="radio" name="end" checked={endMode === "never"} onChange={() => setEndMode("never")} />
                <span>{t("tm.recurrence.never", "Never")}</span>
              </label>
              <label style={radioRow}>
                <input type="radio" name="end" checked={endMode === "count"} onChange={() => setEndMode("count")} />
                <span>{t("tm.recurrence.after", "After")}</span>
                <input
                  type="number"
                  min="1"
                  max="200"
                  className="input"
                  value={count}
                  onChange={(e) => setCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  disabled={endMode !== "count"}
                  style={{ width: 80 }}
                />
                <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>{t("tm.recurrence.occurrences", "occurrence(s)")}</span>
              </label>
              <label style={radioRow}>
                <input type="radio" name="end" checked={endMode === "until"} onChange={() => setEndMode("until")} />
                <span>{t("tm.recurrence.on", "On")}</span>
                <input
                  type="date"
                  className="input"
                  value={until}
                  onChange={(e) => setUntil(e.target.value)}
                  disabled={endMode !== "until"}
                />
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const panel: React.CSSProperties = {
  background: "var(--surface-2)", border: "1px solid var(--border)",
  borderRadius: 8, padding: 12, display: "flex", flexDirection: "column", gap: 12,
};
const row: React.CSSProperties = { display: "flex", gap: 12, flexWrap: "wrap" };
const field: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4, fontSize: 13, minWidth: 120 };
const fieldLabel: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 700, color: "var(--ink-3)",
  letterSpacing: ".04em", textTransform: "uppercase", marginBottom: 4,
};
const radioRow: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--ink-2)",
};
